import axios from 'axios';

// Use relative paths for API calls - works on both preview and production
const API_BASE_URL = '';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Properties
export const getProperties = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.region) params.append('region', filters.region);
  if (filters.country) params.append('country', filters.country);
  if (filters.minPrice) params.append('min_price', filters.minPrice);
  if (filters.maxPrice) params.append('max_price', filters.maxPrice);
  if (filters.propertyType) params.append('property_type', filters.propertyType);
  if (filters.search) params.append('search', filters.search);
  
  const response = await api.get(`/properties?${params.toString()}`);
  return response.data;
};

export const getProperty = async (propertyId) => {
  const response = await api.get(`/properties/${propertyId}`);
  return response.data;
};

export const getPropertyInsights = async (propertyId) => {
  const response = await api.get(`/properties/${propertyId}/insights`);
  return response.data;
};

export const getPropertyMicromarket = async (propertyId) => {
  const response = await api.get(`/properties/${propertyId}/micromarket`);
  return response.data;
};

// Visas
export const getVisas = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.country) params.append('country', filters.country);
  if (filters.region) params.append('region', filters.region);
  if (filters.visaType) params.append('visa_type', filters.visaType);
  
  const response = await api.get(`/visas?${params.toString()}`);
  return response.data;
};

export const getVisa = async (visaId) => {
  const response = await api.get(`/visas/${visaId}`);
  return response.data;
};

// Regions & Countries
export const getRegions = async () => {
  const response = await api.get('/regions');
  return response.data;
};

export const getCountries = async () => {
  const response = await api.get('/countries');
  return response.data;
};

// Leads
export const submitLead = async (leadData) => {
  const response = await api.post('/leads', leadData);
  return response.data;
};

// Newsletter
export const subscribeNewsletter = async (email) => {
  const response = await api.post('/newsletter/subscribe', { email });
  return response.data;
};

// Chat
export const sendChatMessage = async (message, sessionId = null) => {
  const response = await api.post('/chat', { 
    message, 
    session_id: sessionId 
  });
  return response.data;
};

// Auth
export const adminLogin = async (email, password) => {
  const response = await api.post('/auth/login', { email, password });
  return response.data;
};

export const adminLogout = async () => {
  const response = await api.post('/auth/logout');
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

// Admin
export const getAdminLeads = async () => {
  const response = await api.get('/admin/leads');
  return response.data;
};

export const getAdminStats = async () => {
  const response = await api.get('/admin/stats');
  return response.data;
};

export const getNewsletterSubscribers = async () => {
  const response = await api.get('/admin/newsletter');
  return response.data;
};

export const downloadLeadsCSV = () => {
  return `${API_BASE_URL}/api/admin/leads/csv`;
};

// Currency
export const getCurrencyRates = async () => {
  const response = await api.get('/currency/rates');
  return response.data;
};

export const convertCurrency = async (amount, fromCurrency, toCurrency) => {
  const response = await api.get('/currency/convert', {
    params: { amount, from_currency: fromCurrency, to_currency: toCurrency }
  });
  return response.data;
};

// Active countries (with visible properties)
export const getActiveCountries = async () => {
  const response = await api.get('/properties/active-countries');
  return response.data;
};

// Dynamic countries, regions, visas, destinations
export const getAllCountries = async () => {
  const response = await api.get('/countries');
  return response.data;
};

export const getActiveRegions = async () => {
  const response = await api.get('/regions/active');
  return response.data;
};

export const getActiveVisas = async () => {
  const response = await api.get('/visas/active');
  return response.data;
};

export const getActiveDestinations = async () => {
  const response = await api.get('/destinations/active');
  return response.data;
};

export const getCountryInfo = async (countryName) => {
  const response = await api.get(`/countries/${encodeURIComponent(countryName)}/info`);
  return response.data;
};

export const getAdminCountries = async () => {
  const response = await api.get('/admin/countries');
  return response.data;
};

export const createCountry = async (countryData) => {
  const response = await api.post('/admin/countries', countryData);
  return response.data;
};

export const updateCountry = async (countryName, updateData) => {
  const response = await api.put(`/admin/countries/${encodeURIComponent(countryName)}`, updateData);
  return response.data;
};

// Admin - Partners (formerly Developers)
export const getAdminPartners = async () => {
  const response = await api.get('/admin/partners');
  return response.data;
};

// Admin - Notifications
export const getAdminNotifications = async () => {
  const response = await api.get('/admin/notifications');
  return response.data;
};

export const getNotificationCount = async () => {
  const response = await api.get('/admin/notifications/count');
  return response.data;
};

export const markNotificationRead = async (notifId) => {
  const response = await api.put(`/admin/notifications/${notifId}/read`);
  return response.data;
};

export const markAllNotificationsRead = async () => {
  const response = await api.put('/admin/notifications/read-all');
  return response.data;
};

export const approveNotification = async (notifId) => {
  const response = await api.post(`/admin/notifications/${notifId}/approve`);
  return response.data;
};

export const rejectNotification = async (notifId) => {
  const response = await api.post(`/admin/notifications/${notifId}/reject`);
  return response.data;
};

export const createPartner = async (partnerData) => {
  const response = await api.post('/admin/partners', partnerData);
  return response.data;
};

export const updatePartner = async (partnerId, updateData) => {
  const response = await api.put(`/admin/partners/${partnerId}`, updateData);
  return response.data;
};

export const deletePartner = async (partnerId) => {
  const response = await api.delete(`/admin/partners/${partnerId}`);
  return response.data;
};

// Partner Auth
export const partnerLogin = async (email, password) => {
  const response = await api.post('/partner/auth/login', { email, password });
  return response.data;
};

export const getPartnerMe = async () => {
  const response = await api.get('/partner/auth/me');
  return response.data;
};

export const getPartnerProperties = async () => {
  const response = await api.get('/partner/properties');
  return response.data;
};

export const createPartnerProperty = async (propertyData) => {
  const response = await api.post('/partner/properties', propertyData);
  return response.data;
};

export const updatePartnerProperty = async (propertyId, updateData) => {
  const response = await api.put(`/partner/properties/${propertyId}`, updateData);
  return response.data;
};

export const togglePartnerPropertyVisibility = async (propertyId) => {
  const response = await api.put(`/partner/properties/${propertyId}/visibility`);
  return response.data;
};

export const getPartnerLeads = async () => {
  const response = await api.get('/partner/leads');
  return response.data;
};

export const unlockPartnerLead = async (leadId) => {
  const response = await api.post('/partner/leads/unlock', { lead_id: leadId });
  return response.data;
};

export const registerPartner = async (data) => {
  const response = await api.post('/partner/register', data);
  return response.data;
};

export const onboardPartner = async (formData) => {
  const response = await api.post('/partner/onboard', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const uploadPropertyImages = async (files) => {
  const fd = new FormData();
  files.forEach((file, i) => fd.append(`image_${i}`, file));
  const response = await api.post('/upload/property-images', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000
  });
  return response.data;
};

export const uploadLayoutPlans = async (files) => {
  const fd = new FormData();
  files.forEach((file, i) => fd.append(`layout_${i}`, file));
  const response = await api.post('/upload/layout-plans', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000
  });
  return response.data;
};


// Brochure upload
export const uploadBrochure = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/upload/brochure', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000
  });
  return response.data;
};

// Smart search
export const smartSearch = async (query) => {
  const response = await api.post('/search/smart', { query });
  return response.data;
};

// Admin - toggle property visibility
export const togglePropertyVisibility = async (propertyId) => {
  const response = await api.put(`/admin/properties/${propertyId}/toggle-visibility`);
  return response.data;
};

export default api;

// Auth - Email Verification & Password Reset
export const verifyAdminEmail = async (email, code) => {
  const response = await api.post('/auth/verify-email', { email, code });
  return response.data;
};

export const resendAdminCode = async (email) => {
  const response = await api.post('/auth/resend-code', { email });
  return response.data;
};

export const adminForgotPassword = async (email) => {
  const response = await api.post('/auth/forgot-password', { email });
  return response.data;
};

export const resetPassword = async (token, email, password) => {
  const response = await api.post('/auth/reset-password', { token, email, password });
  return response.data;
};

export const verifyPartnerEmail = async (email, code) => {
  const response = await api.post('/partner/auth/verify-email', { email, code });
  return response.data;
};

export const resendPartnerCode = async (email) => {
  const response = await api.post('/partner/auth/resend-code', { email });
  return response.data;
};

export const partnerForgotPassword = async (email) => {
  const response = await api.post('/partner/auth/forgot-password', { email });
  return response.data;
};


// Image thumbnail helper — converts storage URL to thumbnail URL
// /api/files/mulberry/property_images/abc.jpg → /api/files/thumb/400/mulberry/property_images/abc.jpg
export const getThumbnailUrl = (url, width = 400) => {
  if (!url) return '';
  if (url.startsWith('/api/files/') && !url.includes('/thumb/')) {
    return url.replace('/api/files/', `/api/files/thumb/${width}/`);
  }
  // External URLs (http) — return as-is (can't resize externally)
  return url;
};
